import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Col, Row } from "reactstrap"
import { Waypoint } from "react-waypoint"
import Trip from "components/trips/Trip"
import Spinner from "components/common/Spinner"
import ZoomButtons from "components/map/ZoomButtons"
import CurrentLocationButton from "components/map/CurrentLocationButton"
import TripMapMarker from "components/trips/TripMapMarker"
import TripMapCluster from "components/trips/TripMapCluster"
import { groupTripsByLocation } from "components/trips/utils"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import Loading from "modules/loader-watchers/Loading"
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api"
import { pick, debounce } from "lodash"
import { formatAddressComponent, getLocalStorageBrowserLocation, currentLocation, updateLocalStorageBrowserLocation } from "helpers/location"
import { isEmptyFields } from "helpers/form"
import { isLastPage } from "helpers/pagination"
import { useTitle } from "hooks/useTitle"
import useGeocoder from "modules/google/hooks/useGeocoder"
import useSearchFilter from "hooks/useSearchFilter"
import { Alert } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
// Redux
import { useDispatch, useSelector } from "react-redux"
import { cleanTrip, cleanTrips, getTrips } from "store/trips"
import { collectionAnySelector, collectionSelector, metadataSelector } from "store/selectors"
import { API_OPTIONS, DEFAULT_LOCATION, DEFAULT_ZOOM, MAP_OPTIONS } from "constants/map"
import { TRIP_FILTERS, TRIP_FILTERS_HELP_ATTRIBUTES } from "constants/trip"
import { OTHER_FILTERS } from "components/filters/constants"
import { cleanedExperiences } from "store/experiences"
import EmptyState from "components/common/EmptyState"
import SortDropdownAntd from "components/trips/filters/TripsSort"
import styles from "./style.module.css"

export default function Trips() {
  useTitle("Search Trips")
  const { t } = useTranslation()
  const { isLoaded } = useJsApiLoader(API_OPTIONS)
  const dispatch = useDispatch()
  const geocoder = useGeocoder(null)
  const headerHeight = useSelector((state) => state.window.headerHeight)
  const trips = useSelector(collectionSelector("trips"))
  const hasTrips = useSelector(collectionAnySelector("trips"))
  const metadata = useSelector(metadataSelector("trips"))
  const [hoverItemId, setHoverItemId] = useState(null)
  const [activeItemId, setActiveItemId] = useState(null)
  const view = useSelector((state) => state.trips.view) // "list" | "map" — toggle lives in the filter bar
  const [sort, setSort] = useState({ price_sort: "", distance_sort: "" })
  const tripMouseOver = useRef(false)
  const [map, setMap] = useState(null)
  const [boundsLatLng, setBoundsLatLng] = useState(null)
  const lastFetchedBounds = useRef(null) // Track the last bounds we fetched with
  const [params, updateSearchFilters, searchUpdatedCallback] = useSearchFilter({
    accessAttributes: TRIP_FILTERS,
    exceptDependencies: TRIP_FILTERS_HELP_ATTRIBUTES,
    additionalDependencies: [boundsLatLng?.northeast_corner, boundsLatLng?.southwest_corner]
  })

  // selectors for all filters
  const priceRange = useSelector((state) => state.trips.priceRange)
  const language = useSelector((state) => state.trips.language)
  const skillLevel = useSelector((state) => state.trips.skillLevel)
  const experiences = useSelector((state) => state.trips.experiences)
  const subcategories = useSelector((state) => state.trips.subcategories)
  const other = useSelector((state) => state.trips.other)
  const tripsSort = useSelector((state) => state.trips.sort)
  const guests = useSelector((state) => state.trips.guests)
  const calendar = useSelector((state) => state.trips.calendar)
  const browserLocation = useMemo(() => getLocalStorageBrowserLocation(), [])
  const paramsLatLng = useMemo(() => (isEmptyFields(params, ["lat", "lng"]) ? null : pick(params, ["lat", "lng"])), [params])
  // The searched place's viewport (e.g. the whole Bahamas chain), serialized as
  // "lat, lng" corner strings. When present we fit the map to it instead of
  // centering on the nominal point at a fixed zoom.
  const paramsBounds = useMemo(() => {
    const { northeast_corner, southwest_corner } = params
    if (!northeast_corner || !southwest_corner) return null
    const [neLat, neLng] = String(northeast_corner).split(",").map((n) => parseFloat(n))
    const [swLat, swLng] = String(southwest_corner).split(",").map((n) => parseFloat(n))
    if ([neLat, neLng, swLat, swLng].some((n) => !Number.isFinite(n))) return null
    return { ne: { lat: neLat, lng: neLng }, sw: { lat: swLat, lng: swLng } }
  }, [params])
  // Has the user already granted location before (cached, real coords — not the Panama
  // default)? If so we can center there immediately. Otherwise start with no center so the
  // map shows a spinner instead of flashing Panama while we ask for permission on mount.
  const hasCachedLocation = !browserLocation.default
  const [centerLatLng, setCenterLatLng] = useState(paramsLatLng || (hasCachedLocation ? browserLocation : null))
  const zoom = map?.zoom || params.zoom || DEFAULT_ZOOM
  const isProgrammaticMove = useRef(false) // Track if map is being moved programmatically

  const tripGroups = useMemo(() => groupTripsByLocation(trips), [trips])

  const updateSearchHandler = useCallback(
    (location) =>
      geocoder({ location }, (place) =>
        updateSearchFilters({
          ...location,
          zoom,
          address: formatAddressComponent(place.address_components, ["locality", "political"]),
          // The user has panned/zoomed away from the searched place, so its viewport
          // is no longer authoritative — clear it so the fit-to-bounds effect doesn't
          // snap the zoom back on the next idle.
          northeast_corner: "",
          southwest_corner: ""
        })
      ),
    [zoom, geocoder, updateSearchFilters]
  )

  // Window resize / drag fires Google's `idle` event many times in a burst, and each
  // tick shifts the bounds by a fraction — defeating the duplicate-fetch guard and spamming the
  // trips API. Debounce the bounds write so a burst collapses into a single fetch once
  // movement settles. Created once so the timer survives re-renders.
  const debouncedSetBounds = useRef(debounce(setBoundsLatLng, 400)).current
  useEffect(() => () => debouncedSetBounds.cancel(), [debouncedSetBounds])

  const setBoundsHandler = useCallback(
    (loadedMap) => {
      const bounds = (loadedMap || map)?.getBounds()
      if (!bounds) return
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const northeast_corner = Object.keys(ne)
        .reduce((acc, key) => acc.concat(ne[key]()), [])
        .join(", ")
      const southwest_corner = Object.keys(sw)
        .reduce((acc, key) => acc.concat(sw[key]()), [])
        .join(", ")
      debouncedSetBounds({ northeast_corner, southwest_corner })
    },
    [map, debouncedSetBounds]
  )

  const onLoad = useCallback((map) => setMap(map), [])
  const onUnmount = useCallback(() => setMap(null), [])

  const onIdleHandler = useCallback(() => {
    const mapCenter = map?.center
    if (!mapCenter) return

    const lat = mapCenter.lat()
    const lng = mapCenter.lng()

    // Skip updating search params if this is a programmatic move
    if (isProgrammaticMove.current) {
      isProgrammaticMove.current = false
      setBoundsHandler(map) // Update bounds - this will trigger searchUpdatedCallback
      return
    }

    updateSearchHandler({ lat, lng })
    setBoundsHandler(map)
  }, [map, updateSearchHandler, setBoundsHandler])
useEffect(() => {
    if (!paramsBounds || !map || !window.google?.maps) return
    isProgrammaticMove.current = true
    const point = paramsLatLng || {
      lat: (paramsBounds.ne.lat + paramsBounds.sw.lat) / 2,
      lng: (paramsBounds.ne.lng + paramsBounds.sw.lng) / 2
    }
    map.fitBounds(new window.google.maps.LatLngBounds(paramsBounds.sw, paramsBounds.ne))
    const listener = window.google.maps.event.addListenerOnce(map, "idle", () => {
      if ((map.getZoom() || 0) > DEFAULT_ZOOM) {
        map.setZoom(DEFAULT_ZOOM)
        map.setCenter(point)
      }
    })
    setCenterLatLng(point)
    return () => listener.remove()
  }, [paramsBounds?.ne?.lat, paramsBounds?.ne?.lng, paramsBounds?.sw?.lat, paramsBounds?.sw?.lng, map]) //eslint-disable-line

  // Center-on-point fallback for searches that carry no viewport (rare) — keyed on
  // the searched address so it fires per search, not on every idle lat/lng tick.
  const lastCenteredAddress = useRef(null)
  useEffect(() => {
    if (paramsBounds || !paramsLatLng) return
    if (params.address && params.address === lastCenteredAddress.current) return
    lastCenteredAddress.current = params.address
    isProgrammaticMove.current = true
    setCenterLatLng(paramsLatLng)
  }, [params.address, paramsBounds, paramsLatLng]) //eslint-disable-line

  // The map lives in a `d-none` column when "list" is active, so Google renders grey
  // tiles until it gets a resize event. Trigger one (and recenter) when switching to map.
  useEffect(() => {
    if (view !== "map" || !map || !window.google?.maps) return
    window.google.maps.event.trigger(map, "resize")
    if (centerLatLng) map.setCenter(centerLatLng)
  }, [view, map]) //eslint-disable-line

  useEffect(() => {
    dispatch(cleanTrip())
    dispatch(cleanedExperiences())
    return () => {
      setMap(null)
      dispatch(cleanTrips())
    }
  }, []) //eslint-disable-line

  // Zoom requested by the location bootstrap (e.g. DEFAULT_LOCATION's wide zoom 4 on the
  // Panama fallback). Applied when the map is ready, covering the case where geolocation
  // resolves before the map has loaded.
  const pendingZoom = useRef(null)

  // On first visit (no searched location in the URL), ask for the browser location:
  //  - already granted before (cached real coords) → centered there already, skip the prompt
  //  - allowed now → center on the user's location and remember it
  //  - denied/unavailable → currentLocation falls back to DEFAULT_LOCATION (Panama)
  useEffect(() => {
    if (paramsLatLng || hasCachedLocation) return
    let mounted = true
    currentLocation((latLng) => {
      if (!mounted) return
      // Only remember a real granted location — never the Panama deny-fallback, so a denial
      // isn't sticky and we re-ask next visit.
      if (!latLng.default) updateLocalStorageBrowserLocation(latLng, () => {})
      isProgrammaticMove.current = true
      setCenterLatLng({ lat: latLng.lat, lng: latLng.lng }) // user's coords, or Panama if denied
      // The user's own coords carry no zoom (keep the street-level default); the Panama
      // fallback carries a wide zoom so the country fits.
      pendingZoom.current = latLng.zoom || null
      if (map && pendingZoom.current) {
        map.setZoom(pendingZoom.current)
        pendingZoom.current = null
      }
    })
    return () => (mounted = false)
  }, []) //eslint-disable-line

  // Apply a bootstrap zoom that resolved before the map was ready.
  useEffect(() => {
    if (map && pendingZoom.current) {
      map.setZoom(pendingZoom.current)
      pendingZoom.current = null
    }
  }, [map])

  const fetchTrips = (nextPage = false) => {
    if (!boundsLatLng?.northeast_corner || !boundsLatLng?.southwest_corner) return

    // Don't fetch if we're in the middle of a programmatic move (waiting for bounds to update)
    if (isProgrammaticMove.current) {
      return
    }

    const payload = {
      northeast_corner: boundsLatLng.northeast_corner,
      southwest_corner: boundsLatLng.southwest_corner,
      guest_count: guests.adults + guests.children === 0 ? "" : guests.adults + guests.children,
      from_date: calendar.from_date,
      to_date: calendar.to_date,
      priceRange: priceRange,
      skill_levels: skillLevel,
      ...(other.includes(OTHER_FILTERS.ALCOHOL_ALLOWED) && { alcohol_allowed: true }),
      ...(other.includes(OTHER_FILTERS.ACCESSIBLE) && { accessibility_enabled: true }),
      ...(other.includes(OTHER_FILTERS.CHILD_FRIENDLY) && { children_allowed: true }),
      category_ids: experiences,
      subcategory_ids: subcategories,
      price_sort: tripsSort.price_sort,
      distance_sort: tripsSort.distance_sort,
      longitude: params.lng,
      latitude: params.lat,
      languages: language
    }

    // Create a key that includes bounds, filters, guests, and dates to prevent true duplicates
    const fetchKey = `${boundsLatLng.northeast_corner}_${boundsLatLng.southwest_corner}_${JSON.stringify({
      priceRange,
      skillLevel,
      other,
      experiences,
      subcategories,
      language,
      price_sort: tripsSort.price_sort,
      distance_sort: tripsSort.distance_sort,
      guest_count: guests.adults + guests.children,
      from_date: calendar.from_date,
      to_date: calendar.to_date
    })}`

    // Don't fetch if nothing has changed (prevents duplicate calls)
    if (!nextPage && lastFetchedBounds.current === fetchKey) {
      console.log("fetchTrips: Skipping - nothing changed")
      return
    }

    console.log("fetchTrips: Making API call with payload:", payload)

    if (!nextPage) {
      window.scrollTo(0, 0)
      lastFetchedBounds.current = fetchKey // Track this fetch
    }
    dispatch(getTrips(nextPage, payload))
  }

  searchUpdatedCallback(() => {
    console.log("searchUpdatedCallback triggered with params:", params)
    fetchTrips()
  })

  // clear filters
  const handleClearFilters = () => {
    updateSearchFilters({})
  }

  useEffect(() => {
    console.log("Filter useEffect triggered:", { priceRange, language, skillLevel, experiences, other })
    // Only fetch if not in the middle of a programmatic move
    if (!isProgrammaticMove.current) {
      fetchTrips()
    }
    // eslint-disable-next-line
  }, [priceRange, language, skillLevel, experiences, subcategories, other, tripsSort.price_sort, tripsSort.distance_sort])

  return (
    <div className="vstack flex-fill bg-light z-0">
      <Row className="flex-fill g-0 z-0">
        <Col
          xs={12}
          md={7}
          className={`vstack z-0 order-md-2 sticky-top ${view === "map" ? "" : styles.mapHidden}`}
          style={{ top: headerHeight, height: `calc(100vh - ${headerHeight}px)` }}
        >
        {isLoaded ? (
          <GoogleMap
            mapContainerClassName="h-100"
            options={{ ...MAP_OPTIONS, clickableIcons: false }}
            center={centerLatLng || paramsLatLng || DEFAULT_LOCATION}
            zoom={zoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onIdle={onIdleHandler}
            onClick={() => !tripMouseOver.current && setActiveItemId(null)}
          >
            <div className="vstack position-absolute p-10 w-100 h-100 gap-10 pointer-events-none">
              <div className="pointer-events-auto hstack gap-2 ms-auto ">
                <CurrentLocationButton />
              </div>
              {centerLatLng ? (
                <>
                  {tripGroups.map((group) =>
                    group.length > 1 ? (
                      <TripMapCluster
                        key={group[0].id}
                        trips={group}
                        hoverItemId={hoverItemId}
                        activeItemId={activeItemId}
                        toggle={setActiveItemId}
                      />
                    ) : (
                      <TripMapMarker
                        trip={group[0]}
                        key={group[0].id}
                        isActive={hoverItemId === group[0].id}
                        toggle={setActiveItemId}
                      />
                    )
                  )}
                  {activeItemId && (
                    <TripMapMarker
                      key={activeItemId}
                      trip={trips.find((trip) => trip.id === activeItemId)}
                      isActive
                      withPreview
                      toggle={setActiveItemId}
                      onMouseOver={() => (tripMouseOver.current = true)}
                      onMouseLeave={() => (tripMouseOver.current = false)}
                    />
                  )}
                </>
              ) : (
                <div className="hstack h-100 p-50">
                  <Spinner className="m-auto" />
                </div>
              )}
              <ZoomButtons />
            </div>
          </GoogleMap>
        ) : (
          <div className="hstack h-100 p-50">
            <Spinner className="m-auto" />
          </div>
        )}
      </Col>
      <Col
        xs={12}
        md={view === "map" ? 5 : 12}
        className={`z-1 order-md-1 ${view === "map" ? "sticky-top overflow-auto" : ""}`}
        style={view === "map" ? { top: headerHeight, height: `calc(100vh - ${headerHeight}px)` } : undefined}
      >
        <FirstLoading name="trips" className="p-20 h-100">
          <Loading name="trips" className="vstack p-20 h-100 bg-white shadow gap-2 overflow-auto" spinnerProps={{ position: "fixed" }}>
            {hasTrips ? (
              <>
                {/*  Header section */}
                <div className="mb-l h-auto">
                  <h4 className="fw-semibold">{t("filterTags.available_experiences")}</h4>
                  {metadata?.miles_away === true && (
                    <Alert
                      className={styles.alertInfo}
                      message={t("filterTags.no_matches_50_miles")}
                      type="info"
                      showIcon
                      closable
                      icon={<InfoCircleOutlined className={styles.infoIcon} />}
                    />
                  )}
                </div>

                <div className="hstack justify-between flex-wrap align-items-center gap-15 mb-xs">
                  <span>{t("filterTags.experiences_found", { count: metadata?.total_results })}</span>
                  <SortDropdownAntd value={sort} onChange={setSort} />
                </div>
                {/* List view: 3-per-row grid. Map view: single column beside the map. */}
                <Row className="g-15">
                  {trips.map((trip) => (
                    <Col
                      xs={12}
                      sm={view === "map" ? 12 : 6}
                      lg={view === "map" ? 12 : 3}
                      key={trip.id}
                      className="d-flex"
                    >
                      <Trip
                        trip={trip}
                        className="w-100"
                        onMouseOver={() => setHoverItemId(trip.id)}
                        onMouseLeave={() => setHoverItemId(null)}
                        withRating
                        isVertical={view === "map"}
                      />
                    </Col>
                  ))}
                </Row>
                {!isLastPage(metadata) && <Waypoint onEnter={() => fetchTrips(true)} />}
              </>
            ) : (
              <EmptyState
                title={t("filterTags.no_experiences_found")}
                description={t("filterTags.no_matches_area")}
                onClear={handleClearFilters}
              />
            )}
          </Loading>
        </FirstLoading>
      </Col>
      </Row>
    </div>
  )
}
